import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Pusher from 'pusher-js'
import * as PusherTypes from 'pusher-js'
import axios from '@/shared/utils/axios'
import type { ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options'
import { pusherConfig } from '@/shared/utils/window'
import { logWithBadge } from '@/shared/utils/logging'

export interface IPusherConfig {
  PUSHER_KEY: string
  PUSHER_CLUSTER: string
  PUSHER_AUTH_URL: string
  PUSHER_HOST?: string
  PUSHER_PATH?: string
  PUSHER_OPTIONS?: PusherTypes.Options
}

export interface IPusherConfigWithTest extends IPusherConfig {
  PUSHER_TEST: IPusherConfig
}

interface IPusherProviderProps {
  children: React.ReactNode
  pusherClient?: Pusher
}

interface IPusherProviderState {
  pusherClient: Pusher | null
  clearAllPusherChannels: () => void
}

const initialState: IPusherProviderState = {
  pusherClient: null,
  clearAllPusherChannels: () => {},
}

const PusherProviderContext = createContext<IPusherProviderState>(initialState)

// Pusher client configuration is mostly a lift of the Stencil / Inertia Pusher implementation.
function createPusherClient(config: IPusherConfig) {
  let options: PusherTypes.Options = {
    cluster: config.PUSHER_CLUSTER,
    wsHost: config.PUSHER_HOST,
    wsPath: config.PUSHER_PATH,
    channelAuthorization: {
      endpoint: config.PUSHER_AUTH_URL,
      transport: 'ajax',
      customHandler: (params, callback) => {
        axios
          .post(config.PUSHER_AUTH_URL, { cluster: config.PUSHER_CLUSTER, ...params })
          .then((response) => callback(null, response.data as ChannelAuthorizationData))
          .catch((reason: unknown) => {
            if (reason instanceof Error) {
              callback(reason, null)
            } else {
              callback(null, null)
            }
          })
      },
    },
  }

  // TODO: cleanup after rollout
  if (config.PUSHER_OPTIONS) {
    options = {
      ...options,
      ...config.PUSHER_OPTIONS,
    }
  } else {
    options = {
      ...options,
      disabledTransports: ['sockjs'],
      disableStats: true,
    }
  }

  logWithBadge('Pusher', 'options:', options)
  return new Pusher(config.PUSHER_KEY, options)
}

function createDualPusherClient(primaryClient: Pusher, secondaryClient: Pusher | null) {
  return new Proxy(primaryClient, {
    get(target, prop) {
      switch (prop) {
        case 'subscribe':
          return (channelName: string) => {
            if (secondaryClient) {
              secondaryClient.subscribe(channelName)
            }
            const primaryChannel = target.subscribe(channelName)

            // Return a proxied channel that handles unsubscribe properly
            // this covers electron/home.tsx.
            return new Proxy(primaryChannel, {
              get(channelTarget, channelProp) {
                if (channelProp === 'unsubscribe') {
                  return () => {
                    // Unsubscribe from both clients when unsubscribe is called on the channel
                    if (secondaryClient) {
                      secondaryClient.unsubscribe(channelName)
                    }
                    return channelTarget.unsubscribe()
                  }
                }
                // For all other properties/methods, delegate to the primary channel
                const value = channelTarget[channelProp as keyof typeof channelTarget]
                return typeof value === 'function' ? (value.bind(channelTarget) as unknown) : value
              },
            })
          }
        case 'unsubscribe':
          // this is already mostly covered by clearPusherClient in inertia/app/app.tsx
          // this covers electron/home/v2/v2.tsx.
          return (channelName: string) => {
            if (secondaryClient) {
              secondaryClient.unsubscribe(channelName)
            }
            return target.unsubscribe(channelName)
          }
      }
      // For all other properties/methods, delegate to the primary client
      const value = target[prop as keyof Pusher]
      return typeof value === 'function' ? value.bind(target) : value
    },
  })
}

export function PusherProvider({ children, pusherClient: providedPusherClient, ...props }: IPusherProviderProps) {
  const primaryPusherClientRef = useRef<Pusher | null>(providedPusherClient ?? null)
  const secondaryPusherClientRef = useRef<Pusher | null>(null)
  const [pusherClient, setPusherClient] = useState<Pusher | null>(providedPusherClient ?? null)

  useEffect(() => {
    const config = pusherConfig()

    if (!config?.PUSHER_KEY) {
      if (!providedPusherClient) {
        setPusherClient(null)
      }
      return
    }

    const newPusherClient = providedPusherClient ?? createPusherClient(config)

    let pusherTestClient: Pusher | null = null
    if (config.PUSHER_TEST) {
      pusherTestClient = createPusherClient(config.PUSHER_TEST)
    }

    const dualPusherClient = createDualPusherClient(newPusherClient, pusherTestClient)

    dualPusherClient.bind_global((eventName: string, data: unknown) => {
      logWithBadge('Pusher event', eventName, data)
    })

    primaryPusherClientRef.current = newPusherClient
    secondaryPusherClientRef.current = pusherTestClient
    setPusherClient(dualPusherClient)

    return () => {
      primaryPusherClientRef.current?.disconnect()
      secondaryPusherClientRef.current?.disconnect()
    }
  }, [providedPusherClient])

  const clearAllPusherChannels = useCallback(() => {
    const primaryChannels = primaryPusherClientRef.current?.allChannels() ?? []
    primaryChannels.forEach((channel) => primaryPusherClientRef.current?.unsubscribe(channel.name))

    const secondaryChannels = secondaryPusherClientRef.current?.allChannels() ?? []
    secondaryChannels.forEach((channel) => secondaryPusherClientRef.current?.unsubscribe(channel.name))
  }, [])

  const value = {
    pusherClient,
    clearAllPusherChannels,
  }

  return (
    <PusherProviderContext.Provider {...props} value={value}>
      {children}
    </PusherProviderContext.Provider>
  )
}

export const usePusher = () => {
  const context = useContext(PusherProviderContext)

  return context
}
